import type { PlaneKey } from './joints'

/**
 * DIE GESETZTE MOTORISIERUNG — Gen5, Stand V0_24 (29.10.2026).
 *
 * Herkunft: „Gen5 Basics – Kinematik und Performance", Tabellen „Motoren",
 * „Antriebe und Reserven am Gelenk", „Stangenantriebe" und „Offene Punkte".
 *
 * WAS DAS HIER VON `drives.ts` UNTERSCHEIDET, und warum es beides gibt.
 * `drives.ts` SCHLÄGT VOR: es nimmt die gerechnete Anforderung je Achse und
 * sucht im Katalog den kleinsten Motor mit einer passenden Übersetzung. Das ist
 * die Antwort auf „was bräuchte man?". Dieses Modul ist die Antwort auf „was
 * ist entschieden?" — die Auswahl des Stands, mit Übersetzung, Abgangsmoment
 * und Masse je Achse.
 *
 * Beides nebeneinander ist der eigentliche Nutzen: wo der Vorschlag von der
 * Entscheidung abweicht, steht eine Frage.
 *
 * 25 ANGETRIEBENE ACHSEN, 27 MOTOREN. Je Bein 6 Achsen mit 7 Motoren (das Knie
 * trägt zwei), je Arm 5 Achsen mit 5 Motoren, Rumpf 3 Achsen mit 3 Motoren.
 * Sprung und Rumpfgelenk sind Parallelkinematiken: zwei Motoren stellen über
 * Stangen beide Achsen; die zweite Achse steht als eigene Zeile, zählt aber
 * keine eigenen Motoren (`sharedWith`). Das Bewegungsmodell führt mehr
 * Winkelfreiheiten — Becken, zwei Rumpfsegmente —, die hier NICHT motorisiert
 * sind. Das ist kein Widerspruch, sondern der Stand.
 */

/* -------------------------------------------------------------------------
   Motoren
   ------------------------------------------------------------------------- */

/**
 * Ein Motor aus der Tabelle des Stands.
 *
 * Die Spalten stehen so im Stand. Was dort fehlt, fehlt auch hier: der
 * Phasenwiderstand und der Dauerstrom. Ohne Widerstand gibt es keine
 * Motorkonstante und keinen Halteverlust — die betroffene Prüfung in
 * `drives.ts` meldet „nicht bewertbar". Was fehlt, steht als Anfragepunkt in
 * `OPEN_QUESTIONS`.
 */
export interface CatalogMotor {
  id: string
  label: string
  /** Statoraussendurchmesser, mm. */
  diameter: number
  /** Statorhöhe, mm. */
  height: number
  /** Dauermoment am Rotor, Nm. */
  momentNominal: number
  /** Spitzenmoment am Rotor, Nm. */
  momentPeak: number
  /** Nenndrehzahl, 1/min. */
  speedNominal: number
  /** Höchstdrehzahl, 1/min. */
  speedMax: number
  /** Rotorträgheit, kg·m². */
  rotorInertia: number
  /** Motormasse, kg. */
  mass: number
  /**
   * Masse des ganzen Aktors mit Getriebe, kg.
   *
   * Belegt ist nur WG_V12 (FMK08523 mit Taumelgetriebe i 25, CAD: 1,41 kg).
   * Die Arm-Aktoren sind daraus skaliert (Stand), der FMK11525-Aktor ist eine
   * ANNAHME: Motormasse plus Getriebeanteil des WG_V12 (0,91 kg).
   */
  actuatorMass: number
  /**
   * Woher die Zahlen kommen. Ein geschätzter und ein Datenblattwert sehen in
   * der Tabelle sonst gleich aus — und genau daran scheitert später die
   * Entscheidung.
   */
  source: 'datenblatt' | 'geschaetzt'
  note: string
}

/**
 * Die Motoren des Stands. Reihenfolge von gross nach klein, keine Rangfolge —
 * wo welcher sitzt, sagt die Matrix unten.
 */
export const CATALOG_MOTORS: readonly CatalogMotor[] = [
  {
    id: 'fmk11525', label: 'FMK11525',
    diameter: 115, height: 26, momentNominal: 3.5, momentPeak: 11.5,
    speedNominal: 3200, speedMax: 3500, rotorInertia: 5.97e-4, mass: 1.28, actuatorMass: 2.19,
    source: 'datenblatt',
    note: 'Datenblatt. Hüft-Nicken („Motor 125"). Aktormasse: Annahme Motor + Getriebeanteil WG_V12.',
  },
  {
    id: 'fmk08523', label: 'FMK08523',
    diameter: 85, height: 23.5, momentNominal: 2.0, momentPeak: 6.0,
    speedNominal: 3500, speedMax: 4000, rotorInertia: 1.1e-4, mass: 0.5, actuatorMass: 1.41,
    source: 'datenblatt',
    note: 'Kinco/Honpine, Leadshine FM2-08523. Aktor WG_V12 mit Taumelgetriebe i 25: 1,41 kg (CAD), Zahnring Ø96.',
  },
  {
    id: 'fmk07019', label: 'FMK07019',
    diameter: 70, height: 19, momentNominal: 1.4, momentPeak: 5.4,
    speedNominal: 3000, speedMax: 4000, rotorInertia: 4.1e-5, mass: 0.28, actuatorMass: 0.96,
    source: 'geschaetzt',
    note: 'Momente und Drehzahlen aus dem Antriebsbericht; Rotorträgheit und Masse geschätzt. Aktormasse aus WG_V12 skaliert.',
  },
  {
    id: 'fmk06018-0020', label: 'FMK06018-0020',
    diameter: 60, height: 18, momentNominal: 1.0, momentPeak: 3.0,
    speedNominal: 2000, speedMax: 2300, rotorInertia: 2.1e-5, mass: 0.19, actuatorMass: 0.7,
    source: 'geschaetzt',
    note: 'Honpine: Dauermoment und Nenndrehzahl. Spitzenmoment (3× Tn), Höchstdrehzahl (+15 %), Trägheit und Masse angenommen.',
  },
  {
    id: 'fmk06018-0026', label: 'FMK06018-0026',
    diameter: 60, height: 18, momentNominal: 0.7, momentPeak: 2.1,
    speedNominal: 3600, speedMax: 4100, rotorInertia: 2.1e-5, mass: 0.19, actuatorMass: 0.7,
    source: 'geschaetzt',
    note: 'Honpine: Dauermoment und Nenndrehzahl. Spitzenmoment (3× Tn), Höchstdrehzahl (+15 %), Trägheit und Masse angenommen.',
  },
] as const

export const MOTOR_BY_ID: ReadonlyMap<string, CatalogMotor> = new Map(
  CATALOG_MOTORS.map((m) => [m.id, m]),
)

/* -------------------------------------------------------------------------
   Die Achsmatrix
   ------------------------------------------------------------------------- */

/**
 * Wie sicher eine Achse ist.
 *
 * `pass`: alle drei Reserven (Spitze, Dauer, Tempo) bei 1,2 oder darüber und
 * keine Annahme in der Kette. `conditional`: eine Reserve darunter — der Stand
 * nennt diese Achsen als Engpässe — oder ein Motorwert nur angenommen.
 * `open`: ein Konstruktionswert fehlt im CAD und ist gesetzt.
 */
export type AxisStatus = 'pass' | 'conditional' | 'open'

export interface SetAxis {
  key: string
  label: string
  /** Wie viele solche Achsen die Maschine hat — links und rechts zählen zwei. */
  count: number
  /** Motoren JE ACHSE. Knie, Sprung und Rumpfgelenk tragen zwei. */
  motorsPerAxis: number
  motorId: string
  /** Getriebeübersetzung. */
  ratio: number
  /**
   * Übersetzung der Stange, falls der Antrieb über Kurbel und Stange geht.
   *
   * Nennwert; bei Kurbelschwingen (Knie, Ellbogen) ist es der Wert, der das
   * Abgangsmoment unten liefert. Die Kurve selbst steht in `rig/ratio.ts`.
   */
  rodRatio?: number
  /** Spitzenmoment am Gelenk, Nm. */
  momentOut: number
  /** Dauermoment am Gelenk, Nm. */
  momentContinuous: number
  /** Höchstgeschwindigkeit am Gelenk, rad/s. */
  speedOut: number
  /** Motormasse JE ACHSE, kg — ohne Getriebe, Gehäuse und Geber. */
  mass: number
  /**
   * Diese Achse teilt sich ihre Motoren mit einer anderen (Parallelkinematik).
   *
   * Die Zeile trägt dann dieselben Motoren wie ihr Partner, zählt sie aber in
   * den Summen nicht noch einmal. Ohne diese Marke stünden am Sprunggelenk
   * vier Motoren statt zwei.
   */
  sharedWith?: string
  status: AxisStatus
  /** Woran die Achse im Modell hängt — `null`, wenn es dort keine gibt. */
  joint: string | null
  plane: PlaneKey | null
  note?: string
}

/** Grad je Sekunde nach rad/s. */
const RAD = (degPerSecond: number): number => (degPerSecond * Math.PI) / 180

/**
 * Die Matrix, Zeile für Zeile — Werte am Gelenk, Wirkungsgrad Getriebe 0,90,
 * Stange 0,97. Die Reserven des Stands stehen in den Kommentaren:
 * Spitze / Dauer / Tempo.
 */
export const SET_AXES: readonly SetAxis[] = [
  {
    key: 'hip-pitch', label: 'Hüft-Nicken (B1)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk11525', ratio: 25, momentOut: 259, momentContinuous: 79, speedOut: RAD(840), mass: 1.28,
    status: 'open', joint: 'hip', plane: 'sag',
    /* Reserve 1,59 / 1,23 / 1,72. */
    note: 'Getriebeübersetzung im CAD unbekannt, 25 angenommen.',
  },
  {
    key: 'hip-roll', label: 'Hüft-Rollen (B2)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk08523', ratio: 33, momentOut: 178, momentContinuous: 59, speedOut: RAD(727), mass: 0.5,
    status: 'conditional', joint: 'hip', plane: 'fro',
    /* Reserve 1,19 / 1,01 / 2,42. */
    note: 'Engpass: Dauerreserve 1,01, Spitze 1,19. Der CAD-Aktor hat i 25; 33 ist gesetzt.',
  },
  {
    key: 'hip-yaw', label: 'Bein-Gier (B3)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk08523', ratio: 25, momentOut: 135, momentContinuous: 45, speedOut: RAD(960), mass: 0.5,
    status: 'pass', joint: 'hip', plane: 'tra',
    /* Reserve 3,75 / 3,21 / 3,20. */
  },
  {
    key: 'knee', label: 'Knie (B5)', count: 2, motorsPerAxis: 2,
    motorId: 'fmk08523', ratio: 46.26, rodRatio: 0.713, momentOut: 346, momentContinuous: 115, speedOut: RAD(728), mass: 1.0,
    status: 'conditional', joint: 'knee', plane: 'sag',
    /* Reserve 1,52 / – / 1,07. */
    note: 'Kurbelschwinge: Stange 0,36 gestreckt, 0,71 bei 60–70°, 0,32 bei 140°; Gesamtübersetzung bis 33. Moment 156 Nm gestreckt, 346 Nm im Maximum, bei 140° noch 45 %; Tempo 728 °/s bei 60°. Engpass Tempo 1,07.',
  },
  {
    key: 'ankle-pitch', label: 'Sprung-Nicken (B7)', count: 2, motorsPerAxis: 2,
    motorId: 'fmk07019', ratio: 26, rodRatio: 1.46, momentOut: 359, momentContinuous: 93, speedOut: RAD(631), mass: 0.56,
    status: 'conditional', joint: 'ankle', plane: 'sag',
    /* Reserve 1,63 / 1,07 / 1,09. */
    note: 'Parallelkinematik, zwei Fussstangen auf das Kreuzgelenk; Stange 1,47 je Kurbel (0,5 bei −40, 2,2 bei +15). Eigengetriebe i 26. Engpass Dauer 1,07 und Tempo 1,09; bei −40 noch 35 % des Moments.',
  },
  {
    key: 'ankle-roll', label: 'Sprung-Rollen (B8)', count: 2, motorsPerAxis: 2,
    motorId: 'fmk07019', ratio: 26, rodRatio: 0.9, momentOut: 221, momentContinuous: 57, speedOut: RAD(1025), mass: 0.56,
    sharedWith: 'ankle-pitch',
    status: 'pass', joint: 'ankle', plane: 'fro',
    note: 'Dieselben zwei Motoren wie das Nicken — zählt keine eigenen.',
  },
  {
    key: 'pelvis-yaw', label: 'Becken-Gier (T1)', count: 1, motorsPerAxis: 1,
    motorId: 'fmk08523', ratio: 25, momentOut: 135, momentContinuous: 45, speedOut: RAD(960), mass: 0.5,
    status: 'pass', joint: 'lumbar', plane: 'tra',
    /* Reserve 3,38 / 2,81 / 3,20. */
    note: 'Das Bewegungsmodell führt die Rotation an der Lende; zugeordnet ist sie dorthin.',
  },
  {
    key: 'torso-pitch', label: 'Rumpf-Nicken (T2)', count: 1, motorsPerAxis: 2,
    motorId: 'fmk07019', ratio: 26, rodRatio: 1.58, momentOut: 387, momentContinuous: 100, speedOut: RAD(584), mass: 0.56,
    status: 'pass', joint: 'lumbar', plane: 'sag',
    /* Reserve 4,0 / 2,9 / 3,9. */
    note: 'Zwei Bauchmotoren über Kurbel und Stange auf den Taillenträger; Gesamtübersetzung 41 (Nullpose).',
  },
  {
    key: 'torso-roll', label: 'Rumpf-Rollen (T3)', count: 1, motorsPerAxis: 2,
    motorId: 'fmk07019', ratio: 26, rodRatio: 1.75, momentOut: 428, momentContinuous: 110, speedOut: RAD(527), mass: 0.56,
    sharedWith: 'torso-pitch',
    status: 'pass', joint: 'lumbar', plane: 'fro',
    note: 'Dieselben zwei Bauchmotoren wie das Nicken; Gesamtübersetzung 45 (Nullpose).',
  },
  {
    key: 'shoulder-pitch', label: 'Schulter vor/zurück (A1)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk07019', ratio: 36, momentOut: 175, momentContinuous: 45, speedOut: RAD(667), mass: 0.28,
    status: 'pass', joint: 'shoulder', plane: 'sag',
    /* Reserve 2,33 / 1,46 / 1,33. */
    note: 'Motor auf der Achse bei x = 125, ragt in die Zentralbox.',
  },
  {
    key: 'shoulder-roll', label: 'Schulter seitlich (A2)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk07019', ratio: 36, momentOut: 175, momentContinuous: 45, speedOut: RAD(667), mass: 0.28,
    status: 'pass', joint: 'shoulder', plane: 'fro',
    /* Reserve 2,33 / 2,67 / 2,22. */
  },
  {
    key: 'upper-arm-yaw', label: 'Oberarm drehen (A3)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk06018-0020', ratio: 26, momentOut: 70, momentContinuous: 23, speedOut: RAD(531), mass: 0.19,
    status: 'conditional', joint: 'shoulder', plane: 'tra',
    /* Reserve 1,80 / 1,46 / 1,47. */
    note: 'Spitzenmoment und Höchstdrehzahl des FMK06018 angenommen.',
  },
  {
    key: 'elbow', label: 'Ellbogen (A4, Bizeps)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk07019', ratio: 50, rodRatio: 0.71, momentOut: 167, momentContinuous: 43, speedOut: RAD(676), mass: 0.28,
    status: 'pass', joint: 'elbow', plane: 'sag',
    /* Reserve 1,99 / 1,40 / 1,50. */
    note: 'Zugstange: 0,33 gestreckt, 0,71 bei 90°, 0,57 bei 130°. Moment 78 Nm gestreckt, 167 Nm bei 90°. Übertragungswinkel ≥ 30° von −129 bis −3.',
  },
  {
    key: 'forearm-yaw', label: 'Unterarm drehen (A5)', count: 2, motorsPerAxis: 1,
    motorId: 'fmk06018-0026', ratio: 10, momentOut: 19, momentContinuous: 6, speedOut: RAD(2460), mass: 0.19,
    status: 'conditional', joint: null, plane: null,
    /* Reserve 3,15 / 2,62 / 5,47. */
    note: 'Kein Handgelenk im Gangmodell — die Achse hat dort keine Kurve. Motorwerte angenommen.',
  },
] as const

/* -------------------------------------------------------------------------
   Summen
   ------------------------------------------------------------------------- */

/** Angetriebene Achsen insgesamt. */
export const SET_AXIS_COUNT = SET_AXES.reduce((a, x) => a + x.count, 0)

/** Zeilen, die eigene Motoren tragen — die geteilten Achsen zählen hier nicht. */
const OWN_MOTORS = SET_AXES.filter((x) => !x.sharedWith)

/** Verbaute Motoren insgesamt — mehr als Achsen, weil das Knie zwei trägt. */
export const SET_MOTOR_COUNT = OWN_MOTORS.reduce((a, x) => a + x.count * x.motorsPerAxis, 0)

/**
 * Summe aller Motormassen, kg.
 *
 * NUR MOTOREN. Kein Getriebe, kein Gehäuse, kein Geber, keine Bremse, keine
 * Leistungselektronik. Der Unterschied zur Aktormasse unten ist genau das, was
 * noch dazukommt — und er ist der Grund, warum beide Zahlen getrennt stehen.
 */
export const SET_MOTOR_MASS = OWN_MOTORS.reduce((a, x) => a + x.mass * x.count, 0)

/**
 * Masse aller Aktoren mit Getriebe, kg — aus `actuatorMass` je Motor.
 *
 * Keine Vorgabe mehr, sondern die Summe des Stands: WG_V12 aus dem CAD, die
 * Arm-Aktoren daraus skaliert, der FMK11525-Aktor angenommen. Die Arm-Aktoren
 * allein kommen auf 8,6 kg — genau die Zahl, die das Massenmodell führt.
 */
export const DRIVE_MASS_TARGET = OWN_MOTORS.reduce((a, x) => {
  const motor = MOTOR_BY_ID.get(x.motorId)
  return a + (motor ? motor.actuatorMass * x.motorsPerAxis * x.count : 0)
}, 0)

/** Zielmasse der ganzen Maschine, kg — das Massenmodell des Stands. */
export const ROBOT_MASS_TARGET = 55

/** Achsen mit eigenen Motoren — über sie verteilt sich das Getriebebudget. */
export const OWN_AXIS_COUNT = OWN_MOTORS.reduce((a, x) => a + x.count, 0)

/**
 * Was je Achse ausser dem Motor noch zu tragen ist, kg.
 *
 * Die Differenz zwischen Aktor- und Motormasse, gleichmässig auf die Achsen
 * mit eigenen Motoren verteilt (die geteilten Zeilen tragen keinen eigenen
 * Aktor). Eine BUDGETIERUNG, keine Auslegung: ein Taumelgetriebe für 259 Nm
 * wiegt nicht dasselbe wie eines für 19 Nm.
 */
export const DRIVE_OVERHEAD_BUDGET = (DRIVE_MASS_TARGET - SET_MOTOR_MASS) / OWN_AXIS_COUNT

/* -------------------------------------------------------------------------
   Offene Punkte und Annahmen
   ------------------------------------------------------------------------- */

/**
 * Die offenen Punkte des Stands.
 *
 * WARUM SIE IN DIE ANWENDUNG GEHÖREN UND NICHT NUR INS DOKUMENT. Einer davon
 * blockiert die Rechnung, die diese Anwendung macht: ohne Phasenwiderstand
 * gibt es keine Motorkonstante und damit keinen Halteverlust. Die
 * Auslegungsseite sagt an den betroffenen Stellen „nicht bewertbar" — und
 * hier steht, was zu klären ist, damit das aufhört.
 */
export interface OpenQuestion {
  key: string
  topic: string
  /** Was offen ist, im Wortlaut des Stands. */
  needed: string
  /** Blockiert dieser Punkt eine Rechnung dieser Anwendung? */
  blocks: boolean
}

export const OPEN_QUESTIONS: readonly OpenQuestion[] = [
  {
    key: 'widerstand',
    topic: 'Phasenwiderstand',
    needed:
      'Für keinen FMK-Motor im Stand. Ohne ihn keine Motorkonstante Km = Kt/√R und kein Halteverlust — Datenblätter bei Honpine anfragen.',
    blocks: true,
  },
  {
    key: 'fmk07019',
    topic: 'FMK07019',
    needed: 'Rotorträgheit (≈ 4,1e-5 kg·m²) und Masse (≈ 0,28 kg) geschätzt — Datenblatt anfragen.',
    blocks: false,
  },
  {
    key: 'fmk06018',
    topic: 'FMK06018',
    needed: 'Spitzenmoment (3× Tn) und Höchstdrehzahl (+15 %) angenommen — Datenblatt anfragen.',
    blocks: false,
  },
  {
    key: 'freigang',
    topic: 'CAD-Freigang',
    needed:
      'Heute enger als die Grenzwinkel des Modells: Rumpf-Nicken −15/+7,5 (Kreuzarm stösst in die Tasche des Torso_Carrier), Hüft-Rollen ±5 (Abtriebsteller Bein-Gier-Aktor 0,3 mm unter dem Rollgehäuse), Sprung-Rollen ±7,5 (Foot_V5 am Schienbein).',
    blocks: false,
  },
  {
    key: 'hueft-nicken',
    topic: 'Hüft-Nicken',
    needed: 'Getriebeübersetzung im CAD unbekannt, 25 angenommen.',
    blocks: false,
  },
  {
    key: 'hueft-rollen',
    topic: 'Hüft-Rollen',
    needed: 'i 33 gesetzt, der CAD-Aktor hat i 25. Dauerreserve nur 1,01.',
    blocks: false,
  },
  {
    key: 'rumpfbeuger',
    topic: 'Rumpfbeuger',
    needed:
      'Kurbeln, Stangen und Anlenkpunkte sind Annahmen; Bauchmotor 1 steht im CAD bei x = −30, das Modell rechnet symmetrisch bei ±40. Der untere Stangenpunkt liegt 8,8 mm ausserhalb der Taillenträger-Kante (x ±33,5) — Lasche nötig.',
    blocks: false,
  },
  {
    key: 'arme',
    topic: 'Arme',
    needed: 'Aktoren, Struktur und Massen ohne CAD; Unterarm und Hand nur als Ersatzstange Ø16 × 256 mm.',
    blocks: false,
  },
  {
    key: 'akku',
    topic: 'Akku',
    needed:
      'Als Ausgleich nur 1,7 kg im Massenmodell. Mit realistischen 3–5 kg wird der Roboter 56–58 kg, oder Arme und Struktur müssen leichter werden.',
    blocks: false,
  },
]

/**
 * Was die Auslegung NICHT rechnen kann, solange die Punkte offen sind.
 *
 * Steht hier und nicht in `drives.ts`, weil es keine Eigenschaft der Rechnung
 * ist, sondern der Datenlage.
 */
export const MISSING_MOTOR_DATA = [
  'Phasenwiderstand R — ohne ihn keine Motorkonstante und kein Halteverlust',
  'Dauerstrom — ohne ihn keine Momentkonstante Kt aus dem Datenblatt',
] as const

/* -------------------------------------------------------------------------
   Bedarf je Gelenk
   ------------------------------------------------------------------------- */

/**
 * Was die Achsen leisten sollen — der Bedarf des Stands.
 *
 * Beine: Gelenkmomente für 4 m/s (Schache 2011, interpoliert) auf 70 kg
 * (55 + 15 kg Nutzlast); Dauer = Spitze × √(Standanteil/2), Standanteil 0,31.
 * Arme: 8 kg in der Hand, 90 mm hinter dem Handgelenk; Spitze = schlimmste
 * statische Haltung × 1,25, Dauer = 8 kg tragen mit 90° Ellbogen; Armschwung
 * 4 m/s ±40° bei 1,45 Hz.
 *
 * Als Korridor geführt, damit ein Bereich eingetragen werden kann, wo der
 * Stand einen nennt; wo er einen Punkt nennt, sind beide Grenzen gleich.
 */
export interface AxisTarget {
  key: string
  label: string
  /** Spitzenmoment am Gelenk, Nm. */
  moment: [number, number]
  /** Dauermoment am Gelenk, Nm — `null`, wo der Stand keines nennt. */
  momentContinuous: number | null
  /** Spitzengeschwindigkeit am Gelenk, rad/s. */
  speed: [number, number]
  note?: string
}

export const AXIS_TARGETS: readonly AxisTarget[] = [
  { key: 'hip-pitch', label: 'Hüft-Nicken', moment: [163, 163], momentContinuous: 64, speed: [RAD(489), RAD(489)] },
  { key: 'hip-roll', label: 'Hüft-Rollen', moment: [150, 150], momentContinuous: 59, speed: [RAD(300), RAD(300)] },
  { key: 'hip-yaw', label: 'Bein-Gier', moment: [36, 36], momentContinuous: 14, speed: [RAD(300), RAD(300)] },
  {
    key: 'knee', label: 'Knie', moment: [228, 228], momentContinuous: null, speed: [RAD(682), RAD(682)],
    note: 'Winkelabhängig: die Kurbelschwinge liefert ihr Maximum um 60°. Ein Dauerwert steht nicht im Stand.',
  },
  { key: 'ankle-pitch', label: 'Sprung-Nicken', moment: [220, 220], momentContinuous: 87, speed: [RAD(581), RAD(581)] },
  { key: 'pelvis-yaw', label: 'Becken-Gier', moment: [40, 40], momentContinuous: 16, speed: [RAD(300), RAD(300)] },
  { key: 'torso-pitch', label: 'Rumpf-Nicken', moment: [96, 96], momentContinuous: 35, speed: [RAD(150), RAD(150)] },
  { key: 'shoulder-pitch', label: 'Schulter vor/zurück', moment: [75, 75], momentContinuous: 31, speed: [RAD(500), RAD(500)] },
  { key: 'shoulder-roll', label: 'Schulter seitlich', moment: [75, 75], momentContinuous: 17, speed: [RAD(300), RAD(300)] },
  { key: 'upper-arm-yaw', label: 'Oberarm drehen', moment: [39, 39], momentContinuous: 16, speed: [RAD(360), RAD(360)] },
  { key: 'elbow', label: 'Ellbogen', moment: [39, 39], momentContinuous: 31, speed: [RAD(450), RAD(450)], note: 'Dauer bei 90°.' },
  { key: 'forearm-yaw', label: 'Unterarm drehen', moment: [6, 6], momentContinuous: 2.4, speed: [RAD(450), RAD(450)] },
]

/** Der Bedarf einer Achse — oder `null`, wenn der Stand keinen nennt. */
export const targetFor = (key: string): AxisTarget | null =>
  AXIS_TARGETS.find((t) => t.key === key) ?? null

/**
 * Die gesetzte Achse zu einem Gelenk und einer Ebene — oder `null`.
 *
 * Der Weg vom Bewegungsmodell in die Entscheidung. Er ist bewusst einseitig:
 * das Modell kennt die Matrix nicht, und es soll sie auch nicht kennen.
 */
export const setAxisFor = (joint: string, plane: PlaneKey): SetAxis | null =>
  SET_AXES.find((a) => a.joint === joint && a.plane === plane) ?? null
